import React, { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { RedButton, GhostButton } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";

const STATUS_LABEL = { confirmed: "CONFIRMÉ", pending: "EN ATTENTE DE VALIDATION", cancelled: "ANNULÉ", done: "TERMINÉ" };
const STATUS_STYLE = {
  confirmed: "border-emerald-600/70 bg-emerald-50 text-emerald-800", pending: "border-amber-500/70 bg-amber-50 text-amber-800",
  cancelled: "border-zinc-300 text-zinc-500", done: "border-zinc-300 text-zinc-600",
};
const frDate = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : "—");

export default function MonRendezVous() {
  const [creds, setCreds] = useState({ reference: "", phone: "" });
  const [appt, setAppt] = useState(null);
  const [busy, setBusy] = useState(false);

  const find = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/appointments/lookup", creds);
      setAppt(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Annuler définitivement ce rendez-vous ?")) return;
    setBusy(true);
    try {
      await api.post("/appointments/cancel", creds);
      toast.success("Rendez-vous annulé");
      setAppt({ ...appt, status: "cancelled" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="mon-rdv-page">
      <PageHero overline="Espace client" title="Mon rendez-vous" />
      <section className="mx-auto max-w-3xl px-4 md:px-8 py-14">
        {!appt && (
          <form onSubmit={find} className="border border-zinc-800 bg-[#0a0a0a] p-7 md:p-10 space-y-6" data-testid="lookup-form">
            <label className="block">
              <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Référence</span>
              <input required data-testid="lookup-reference" value={creds.reference} placeholder="SA-XXXXXX"
                     onChange={(e) => setCreds({ ...creds, reference: e.target.value })}
                     className="mt-2 w-full bg-black border border-zinc-800 px-4 py-3 text-white focus:border-sared focus:outline-none" />
            </label>
            <label className="block">
              <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Téléphone</span>
              <input required data-testid="lookup-phone" value={creds.phone}
                     onChange={(e) => setCreds({ ...creds, phone: e.target.value })}
                     className="mt-2 w-full bg-black border border-zinc-800 px-4 py-3 text-white focus:border-sared focus:outline-none" />
            </label>
            <RedButton type="submit" disabled={busy} data-testid="lookup-submit">
              {busy ? "Recherche..." : "Retrouver mon rendez-vous"}
            </RedButton>
          </form>
        )}

        {appt && (
          <div className="border border-sared bg-black p-7 md:p-10" data-testid="appointment-detail">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="overline mb-2">Référence</div>
                <div className="font-head text-4xl text-sared">{appt.reference}</div>
              </div>
              <span className={`font-mono-tech text-[10px] tracking-[0.18em] px-3 py-2 border ${STATUS_STYLE[appt.status] || STATUS_STYLE.pending}`}
                    data-testid="appointment-status">{STATUS_LABEL[appt.status] || "EN ATTENTE"}</span>
            </div>

            <div className="mt-8 grid gap-3 text-sm">
              {[["Date", frDate(appt.date)], ["Créneau", `${appt.start} – ${appt.end}`],
                ["Prestation", appt.service_text], ["Description", appt.description || "—"],
                ["Véhicule", `${appt.vehicle_brand || ""} ${appt.vehicle_model || ""}`.trim() || "—"],
                ["Immatriculation", appt.plate || "—"],
                ["Client", [appt.first_name, appt.last_name].filter(Boolean).join(" ")],
                ["Téléphone", appt.phone]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-zinc-900 pb-2">
                  <span className="text-zinc-500 font-mono-tech text-[10px] tracking-[0.16em] uppercase">{k}</span>
                  <span className="text-white text-right">{v}</span>
                </div>
              ))}
            </div>

            {appt.status !== "cancelled" && (
              <div className="mt-9 flex flex-wrap gap-4">
                <GhostButton onClick={cancel} disabled={busy} data-testid="cancel-btn">Annuler ma demande</GhostButton>
              </div>
            )}

            <button onClick={() => setAppt(null)}
                    className="mt-8 font-mono-tech text-[10px] tracking-[0.18em] text-zinc-500 hover:text-sared"
                    data-testid="lookup-reset">← CHERCHER UN AUTRE RENDEZ-VOUS</button>
          </div>
        )}
      </section>
    </div>
  );
}
